import * as Sentry from "@sentry/angular";
import { Injectable, signal, inject } from "@angular/core";
import { ObservabilityDataService } from "./observability-data.service";
import { firstValueFrom } from "rxjs";

// A single captured missing-translation label, reported to Sentry by the i18n
// miss-catcher. A plain shape on purpose: the dev-only miss-catcher maps its own
// MissRecord onto this so it never has to import @sentry/* (keeps that tool isolated).
export interface I18nMissReport {
    text: string;
    slot: string;
    selector: string | null;
    selectorChain: string[];
    routes: string[];
    breadcrumb: string;
    count: number;
}

// Spotlight-only local dev: the SDK refuses to initialize without a DSN, so give it this local
// placeholder. It mirrors the backend's placeholder; the tunnel rejects its host so nothing reaches
// sentry.io, and only the Spotlight sidecar receives events.
const SPOTLIGHT_PLACEHOLDER_DSN = "https://spotlight@localhost/0";

// Tag naming the tenant an event belongs to. It must stay equal to the backend's
// CerrixDiagnostics.TenantAttribute, which the backend uses on its Sentry scope, its spans and its
// log scopes. A different name here would split one tenant's events over two search terms.
export const TENANT_TAG = "cerrix.grc.tenant";

@Injectable({
    providedIn: "root",
})
export class ObservabilityService {
    private observabilityDataService = inject(ObservabilityDataService);

    private readonly sentryErrorHandler = Sentry.createErrorHandler();

    // Whether Sentry is initialized (global config enabled + DSN present). Drives the
    // i18n miss-catcher's "Submit to Sentry" button; false → fall back to JSON export.
    public readonly sentryEnabled = signal(false);

    // Whether Sentry bug reporting is enabled for the current user. Drives the
    // navbar bug-report button (NavbarBugReportComponent).
    public readonly bugReportingEnabled = signal(false);
    private feedbackForm?: Awaited<
        ReturnType<NonNullable<ReturnType<typeof Sentry.getFeedback>>["createForm"]>
    >;

    async initGlobal(): Promise<void> {
        try {
            const config = await firstValueFrom(this.observabilityDataService.getGlobalConfig());

            // Spotlight-only local dev keeps all telemetry on the box, so it does not need a real DSN
            // or the upstream tunnel. Upstream (sentry.io) still requires both.
            if (!config.spotlight && (!config.enabled || !config.dsn)) return;

            Sentry.init({
                // The SDK needs a DSN to initialize. In Spotlight-only mode use a placeholder: envelopes
                // go to the same-origin tunnel below, which rejects the placeholder host and forwards
                // nothing to sentry.io, while spotlightBrowserIntegration streams a copy to the sidecar.
                dsn: config.spotlight ? SPOTLIGHT_PLACEHOLDER_DSN : config.dsn,
                tunnel: "/api/sentry/tunnel",
                environment: config.environment || "unknown",
                initialScope: { tags: { [TENANT_TAG]: config.tenant } },
                // spanStreamingIntegration enables stream mode, required for ignoreSpans.
                // It becomes the SDK default in v11, so this is forward-compatible.
                // Skip it in Spotlight mode: stream mode emits span-v2 envelopes that the local
                // Spotlight sidecar does not render as traces. Classic transaction envelopes show up.
                integrations: [
                    Sentry.browserTracingIntegration({
                        // Do not trace the SDK's own envelope POSTs to the tunnel; they create
                        // self-referential http.client spans that clutter Spotlight.
                        shouldCreateSpanForRequest: (url) => !url.includes("/api/sentry/tunnel"),
                    }),
                    ...(config.spotlight ? [] : [Sentry.spanStreamingIntegration()]),
                    Sentry.replayIntegration(),
                    Sentry.captureConsoleIntegration({ levels: ["error"] }),
                    // Local-dev only: forward errors and traces to the Spotlight sidecar.
                    ...(config.spotlight
                        ? [Sentry.spotlightBrowserIntegration({ sidecarUrl: config.spotlightUrl })]
                        : []),
                ],
                // Drop low-value auto-instrumented browser spans to stay within our span
                // budget. Keeps the useful skeleton (pageload, ui.angular.routing, http.client).
                ignoreSpans: [
                    { op: "mark" },
                    { op: "measure" },
                    { op: "paint" },
                    { op: /^resource\./ },
                    { op: /^browser\./ },
                ],
                // The backend decides the sample rate (api/sentry/config); 0.2 is only a
                // defensive fallback. Use ?? so a deliberate 0 from the backend is respected.
                // Spotlight wants full local fidelity, so record every trace in that mode.
                tracesSampleRate: config.spotlight ? 1.0 : (config.tracesSampleRate ?? 0.2),
                replaysSessionSampleRate: 0.1,
                replaysOnErrorSampleRate: 1,
                enableLogs: true,
                sendDefaultPii: false,
            });
            this.sentryEnabled.set(true);
        } catch {
            console.warn("[Observability] Failed to initialize Sentry global config.");
        }
    }

    async initUser(): Promise<void> {
        try {
            const config = await firstValueFrom(this.observabilityDataService.getUserConfig());
            Sentry.setUser({
                id: config.userId || "unknown",
            });

            if (config.enableBugReporting) {
                // autoInject: false → no floating button; the trigger lives in the navbar
                // (NavbarBugReportComponent), opened via openBugReport().
                Sentry.getClient()?.addIntegration(Sentry.feedbackIntegration({ autoInject: false }));
                this.bugReportingEnabled.set(true);
            }
        } catch {
            console.warn("[Observability] Failed to initialize Sentry user config.");
        }
    }

    // Opens the Sentry feedback form from the navbar bug-report button. The form is built
    // lazily once and reused so repeated clicks don't append duplicate forms to the DOM.
    async openBugReport(): Promise<void> {
        const feedback = Sentry.getFeedback();
        if (!feedback) return;
        if (!this.feedbackForm) {
            this.feedbackForm = await feedback.createForm();
            this.feedbackForm.appendToDom();
        }
        this.feedbackForm.open();
    }

    async logoutUser(): Promise<void> {
        Sentry.setUser(null);
    }

    async logError(error: Error): Promise<void> {
        this.sentryErrorHandler.handleError(error);
    }

    // Reports captured missing i18n labels to Sentry, one event per label. A per-label
    // fingerprint makes each label a single deduped Issue (resubmits increment its count
    // instead of creating new Issues). Safe no-op when Sentry is not initialized.
    reportI18nMisses(misses: I18nMissReport[]): number {
        if (!Sentry.getClient()) return 0;
        for (const m of misses) {
            Sentry.withScope((scope) => {
                scope.setLevel("info");
                scope.setFingerprint(["i18n-missing", m.selector ?? "?", m.slot, m.text]);
                scope.setTag("i18n_missing", "true");
                scope.setTag("i18n_selector", m.selector ?? "unknown");
                scope.setTag("i18n_slot", m.slot);
                scope.setContext("i18n-miss", {
                    text: m.text,
                    selectorChain: m.selectorChain,
                    routes: m.routes,
                    breadcrumb: m.breadcrumb,
                    count: m.count,
                });
                Sentry.captureMessage(`Missing i18n label: ${m.text}`);
            });
        }
        return misses.length;
    }
}
